import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";
import { PaginationProps } from "./requests";

export interface WebhookState {
  id: string;
  created_by: string;
  last_updated_by: string;
  datetime_created: string;
  datetime_updated: string | null;
  name: string;
  action: [string, string];
  integration_entity: { id: string; name: string };
  url: string;
  method: [string, string];
  auth_type: string;
  auth_in: string;
  auth_key_name: string;
  auth_key: string;
  placeholders: { url: { [key: string]: any }; body: { [key: string]: any } };
  body: string;
  status: [string, string];
}

export interface WebhookHistoryState {
  id: string;
  datetime_created: string;
  webhook: string;
  action: [string, string];
  integration_entity: string;
  status: [string, string];
  sent_body: any;
  response_on_send: any;
  received_body: any;
}

export interface IntegrationState {
  webhooks: {
    data: WebhookState[] | null;
    history: {
      data: WebhookHistoryState[] | null;
      pagination: PaginationProps;
      loading: boolean;
    };
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: IntegrationState = {
  webhooks: {
    data: null,
    history: {
      data: null,
      pagination: {
        has_previous: false,
        has_next: false,
        current_page: 1,
        total_pages: 1,
        total_objects: 0,
      },
      loading: false,
    },
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get webhooks
export const getWebhooks = createAsyncThunk(
  "integration/webhooks/get",
  async (
    obj: {
      action: string | null;
      method: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/webhook/`;
      let check = false;
      if (obj.action) {
        url += `?action=${obj.action}`;
        check = true;
      }
      if (obj.method) {
        url += `${check ? "&" : "?"}method=${obj.method}`;
      }

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create webhook
export const createWebhook = createAsyncThunk(
  "integration/webhooks/post",
  async (
    obj: {
      name: string;
      action: string;
      property: string;
      integrationEntity: string;
      url: string;
      method: string;
      authType: string;
      authIn: string;
      authKeyName: string;
      authKey: string;
      placeholders: { [key: string]: any };
      body: { [key: string]: any };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        action: obj.action,
        property: obj.property,
        integration_entity: obj.integrationEntity,
        url: obj.url,
        method: obj.method,
        auth_type: obj.authType,
        auth_in: obj.authIn,
        auth_key_name: obj.authKeyName,
        auth_key: obj.authKey,
        placeholders: obj.placeholders,
        body: obj.body,
        status: obj.status,
      };

      // Build URL
      const url = "/api/webhook/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update webhook
export const updateWebhook = createAsyncThunk(
  "integration/webhooks/put",
  async (
    obj: {
      webhookId: string;
      name: string;
      action: string;
      property: string;
      integrationEntity: string;
      url: string;
      method: string;
      authType: string;
      authIn: string;
      authKeyName: string;
      authKey: string;
      placeholders: { [key: string]: any };
      body: { [key: string]: any };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.webhookId,
        name: obj.name,
        action: obj.action,
        property: obj.property,
        integration_entity: obj.integrationEntity,
        url: obj.url,
        method: obj.method,
        auth_type: obj.authType,
        auth_in: obj.authIn,
        auth_key_name: obj.authKeyName,
        auth_key: obj.authKey,
        placeholders: obj.placeholders,
        body: obj.body,
        status: obj.status,
      };

      // Build URL
      const url = "/api/webhook/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete webhooks
export const deleteWebhooks = createAsyncThunk(
  "integration/webhooks/delete",
  async (
    obj: {
      webhookIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.webhookIds },
      };

      const res = await axios.delete("/api/webhook/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get webhook history
export const getWebhookHistory = createAsyncThunk(
  "integration/webhooks/history/get",
  async (
    obj: {
      page: number | boolean;
      action: string | null;
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      let params: { [key: string]: any } = {
        page: obj.page,
        action: obj.action,
        dateRange: obj.dateRange,
      };
      params = Object.fromEntries(
        Object.entries(obj).filter(([_, v]) => v != null),
      );
      const config = {
        headers: { Authorization: `Bearer ${auth.access}` },
        params,
      };

      // Build endpoint URL
      let url = `/api/webhook/history/`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const integrationSlice = createSlice({
  name: "integration",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get webhooks
      .addCase(getWebhooks.pending, (state) => {
        state.webhooks.loading = true;
        state.webhooks.waiting = false;
      })
      .addCase(getWebhooks.fulfilled, (state, action) => {
        state.webhooks.data = action.payload.data.data;
        state.options.actions = action.payload.data.options.actions;
        state.options.integration_entities =
          action.payload.data.options.integration_entities;
        state.options.methods = action.payload.data.options.methods;
        state.options.auth_type = action.payload.data.options.auth_type;
        state.options.auth_in = action.payload.data.options.auth_in;
        state.options.auth_key_name = action.payload.data.options.auth_key_name;
        state.options.placeholders = action.payload.data.options.placeholders;
        state.webhooks.loading = false;
        state.webhooks.waiting = false;
      })
      .addCase(getWebhooks.rejected, (state) => {
        state.webhooks.loading = false;
        state.webhooks.waiting = false;
      })

      // Create webhook
      .addCase(createWebhook.pending, (state) => {
        state.webhooks.loading = true;
        state.webhooks.waiting = false;
      })
      .addCase(createWebhook.fulfilled, (state, action) => {
        state.webhooks.data =
          state.webhooks.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.webhooks.data];
        state.webhooks.loading = false;
        state.webhooks.waiting = false;
      })
      .addCase(createWebhook.rejected, (state) => {
        state.webhooks.loading = false;
        state.webhooks.waiting = false;
      })

      // Update webhook
      .addCase(updateWebhook.pending, (state) => {
        state.webhooks.waiting = true;
        state.webhooks.loading = true;
      })
      .addCase(updateWebhook.fulfilled, (state, action) => {
        const previousData =
          state.webhooks.data === null ? [] : state.webhooks.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const menu = previousData[i];
          if (menu.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(menu);
          }
        }
        state.webhooks.data = newData;
        state.webhooks.loading = false;
        state.webhooks.waiting = false;
      })
      .addCase(updateWebhook.rejected, (state) => {
        state.webhooks.waiting = false;
        state.webhooks.loading = false;
      })

      // Delete webhooks
      .addCase(deleteWebhooks.pending, (state) => {
        state.webhooks.loading = true;
        state.webhooks.waiting = false;
      })
      .addCase(deleteWebhooks.fulfilled, (state, action) => {
        const existingData =
          state.webhooks.data === null ? [] : state.webhooks.data;
        const updatedData = existingData.filter(
          (hook) => !action.payload.data.id.includes(hook.id),
        );
        state.webhooks.data = updatedData;
        state.webhooks.loading = false;
        state.webhooks.waiting = false;
      })
      .addCase(deleteWebhooks.rejected, (state) => {
        state.webhooks.loading = false;
        state.webhooks.waiting = false;
      })

      // Get webhook history
      .addCase(getWebhookHistory.pending, (state) => {
        state.webhooks.history.loading = true;
      })
      .addCase(getWebhookHistory.fulfilled, (state, action) => {
        state.webhooks.history.data = action.payload.data.data;
        state.options.actions = action.payload.data.options.actions;
        state.options.methods = action.payload.data.options.methods;
        state.webhooks.history.pagination = action.payload.data.pagination;
        state.webhooks.history.loading = false;
      })
      .addCase(getWebhookHistory.rejected, (state) => {
        state.webhooks.history.loading = false;
      });
  },
});

export default integrationSlice.reducer;
